import { useRouter } from 'next/router';
import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import { DateFormat, IKeyAnyValue } from 'common/types';
import { Days, days } from 'common/utils';
import config from 'config/index';
import usePrevious from 'hooks/usePrevious';
import lastDepartureSearchStorage from 'storage/lastDepartureSearch';

enum TripType {
  RT = 'RT', // 왕복
  OW = 'OW', // 편도
  MD = 'MD', // 다구간
  EVERYWHERE = 'EVERYWHERE',
  NULL = 'NULL',
}

enum SearchType {
  INTERNATIONAL = 'international',
  DOMESTIC = 'domestic',
  EVERYWHERE = 'everywhere',
}

export enum ScheduleType {
  DEPARTURE = 'departure',
  ARRIVAL = 'arrival',
}

enum SeatClass {
  Y = 'Y', // 일반석
  P = 'P', // 프리미엄 일반석
  C = 'C', // 비즈니스석
  F = 'F', // 일등석
  YC = 'YC', // '전체
}

export interface ICity {
  code: string; // 공항 iataCode
  name: string; // 도시 이름
  isDomestic: boolean; // 국내 여부
  cityCode?: string; // 도시 코드
}

export interface ISearchParamItinerary {
  arrivalAirport?: string;
  arrivalCity?: ICity;
  departureAirport?: string;
  departureCity?: ICity;
  departureDate?: string;
}

export interface IPassengers {
  [personType: string]: number;
  adult: number;
  child: number;
  infant: number;
}

interface IDateFormatInfo {
  [key: string]: { from: number; count: number };
}

const AP_CODES = {
  INCHEON: 'ICN',
  GIMPO: 'GMP',
  BOTH: 'ICN_GMP',
  SEOUL: 'SEL',
  EVERYWHERE: 'ALL',
};

const AIRPORTS = {
  INCHEON: {
    code: AP_CODES.INCHEON,
    name: '인천',
    isDomestic: true,
    cityCode: AP_CODES.INCHEON,
  },
  GIMPO: {
    code: AP_CODES.GIMPO,
    name: '김포',
    isDomestic: true,
    cityCode: AP_CODES.GIMPO,
  },
  SEOUL: { code: AP_CODES.SEOUL, name: '서울', isDomestic: true },
  EVERYWHERE: { code: AP_CODES.EVERYWHERE, name: '모든 곳', isDomestic: false },
};

const EXCEPTIONAL_AP_CODES: IKeyAnyValue = {
  KIX: AP_CODES.BOTH,
  HND: AP_CODES.BOTH,
  PEK: AP_CODES.BOTH,
  TSA: AP_CODES.GIMPO,
  SHA: AP_CODES.GIMPO,
};

const domesticSeatClasses = new Map([
  [SeatClass.YC, '전체'],
  [SeatClass.Y, '할인/일반/특가석'],
  [SeatClass.C, '비즈니스석'],
]);

export const internationalSeatClasses = new Map([
  [SeatClass.Y, '일반석'],
  [SeatClass.P, '프리미엄 일반석'],
  [SeatClass.C, '비즈니스석'],
  [SeatClass.F, '일등석'],
]);

const searchTypeKoreanMap = new Map([
  [SearchType.INTERNATIONAL, '국제선'],
  [SearchType.DOMESTIC, '국내선'],
]);

export const tripTypes = new Map([
  [TripType.RT, '왕복'],
  [TripType.OW, '편도'],
  [TripType.MD, '다구간'],
]);

const DateFormatInfos = [
  DateFormat.YMD,
  DateFormat.DotYMD,
  DateFormat.DotYMDd,
  DateFormat.DotYMD_d,
  DateFormat.DotMDd,
  DateFormat.DotMD_d,
].reduce<Map<DateFormat, IDateFormatInfo>>((acc, format) => {
  acc.set(
    format,
    format.split('').reduce<{ [key: string]: any }>((res, f, i) => {
      if (!['Y', 'M', 'D', 'd'].includes(f)) return res;
      if (!res[f]) res[f] = { from: i, count: 1 };
      else res[f].count += 1;
      return res;
    }, {}),
  );
  return acc;
}, new Map());

// rough한 validation 체크.
const EMPTY_FORMAT = { from: 0, count: 0 };

const getTargetValue = (date: string, from: number, count: number) =>
  date.slice(from, from + count);

const getTargetValues = (
  date: string,
  infos: { from: number; count: number }[],
) => infos.map(({ from, count }) => getTargetValue(date, from, count));

const validDateFormatInfo: { [key: string]: any } = {
  Y: (date: string, dateFormatInfo: IDateFormatInfo) => {
    const {
      Y: { from, count },
    } = dateFormatInfo;
    const target = getTargetValue(date, from, count);
    return (
      target.length === count &&
      target >= `${new Date().getFullYear()}`.slice(-count)
    );
  },
  M: (date: string, dateFormatInfo: IDateFormatInfo) => {
    const { Y = EMPTY_FORMAT, M } = dateFormatInfo;
    const today = new Date();
    const [targetYear, targetMonth] = getTargetValues(date, [Y, M]);
    if (
      +targetMonth < 1 ||
      +targetMonth > 12 ||
      (targetYear &&
        +targetYear <= today.getFullYear() &&
        +targetMonth <= today.getMonth())
    )
      return false;
    if (M.count === 1) return targetMonth.length <= 2;
    if (M.count === 2) return targetMonth.length === 2;
    return false;
  },
  D: (date: string, dateFormatInfo: IDateFormatInfo) => {
    const { Y = EMPTY_FORMAT, M = EMPTY_FORMAT, D } = dateFormatInfo;
    const today = new Date();
    const [targetYear, targetMonth, targetDate] = getTargetValues(date, [
      Y,
      M,
      D,
    ]);
    if (+targetDate < 1) return false;
    if (
      targetMonth &&
      (([1, 3, 5, 7, 8, 10, 12].includes(+targetMonth) && +targetDate > 31) ||
        ([4, 6, 9, 11].includes(+targetMonth) && +targetDate > 30) ||
        (+targetMonth === 2 && +targetYear % 4 !== 0 && +targetDate > 28) ||
        (+targetMonth === 2 && +targetYear % 4 === 0 && +targetDate > 29) ||
        (targetYear &&
          +targetYear <= today.getFullYear() &&
          +targetMonth <= today.getMonth() &&
          +targetDate <= today.getDate()))
    )
      return false;
    if (D.count === 1) return targetDate.length <= 2;
    if (D.count === 2) return targetDate.length === 2;
    return false;
  },
  d: () => true,
};

const getValidDate = (
  d: string | Days | undefined,
  format: DateFormat = DateFormat.YMD,
) => {
  if (!d) return undefined;
  const date = days(d, DateFormat.YMD).format(DateFormat.YMD);
  const formatInfo = DateFormatInfos.get(format);
  if (!date || date.length !== format.length || !formatInfo) return false;
  return (
    Object.keys(formatInfo).every(
      (key: string) =>
        validDateFormatInfo[key] && validDateFormatInfo[key](date, formatInfo),
    ) && date
  );
};

const stringifyItinerary = (
  itinerary: ISearchParamItinerary[],
  stayLength?: string,
) =>
  itinerary
    .map(
      ({ departureCity, arrivalCity, departureDate }, index) =>
        `${departureCity?.code}-${arrivalCity?.code}-${
          stayLength && index === 1 ? stayLength : departureDate
        }`,
    )
    .join('/');

const getValidDates = (
  ds: (string | Days | undefined)[],
  format: DateFormat = DateFormat.YMD,
) => {
  const res: (string | undefined)[] = [];
  for (const date of ds) {
    const d = getValidDate(date, format);
    if (d === false) break;
    res.push(d);
  }
  return ds.length === res.length && res;
};

const getPassengerDesc = (passengers: IPassengers) =>
  `${
    passengers.child || passengers.infant ? '승객' : '성인'
  } ${Object.values(passengers).reduce((prev, count) => prev + count, 0)}명`;

const getSeatClassMap = (isDomestic: boolean) =>
  isDomestic ? domesticSeatClasses : internationalSeatClasses;

const getSeatDesc = (isDomestic: boolean, seatType: string) => {
  if (seatType in SeatClass) {
    const seatDescMap = getSeatClassMap(isDomestic);
    return seatDescMap.get(seatType as SeatClass);
  }
  return null;
};

const changeToGimpo = (itinerary: ISearchParamItinerary[]) => {
  const newItinerary = [...itinerary];
  newItinerary[0] = { ...newItinerary[0], departureCity: AIRPORTS.GIMPO };
  if (itinerary[1])
    newItinerary[1] = { ...newItinerary[1], arrivalCity: AIRPORTS.GIMPO };
  return newItinerary;
};

const getExceptionHandledItinerary = (
  itinerary: ISearchParamItinerary[],
  tripType: TripType,
  targetCity: ICity,
) => {
  /*
    예외처리: '도착지' 설정 시점에만 적용!
    '출발지'가 '인천'인 경우에 한해 '김포'로 변경해야 하는 케이스가 있다.

    1. 그대로 유지하는 케이스:
      a. 편도: 출발지/도착지 모두 '인천'
      b. 떙처리. 다구간
      c. 출발지 특정한 경우
    2. 예외) 도착지가 KIX, HND, PEK    : 그대로 유지 (ICN/GMP 모두 출항 존재)
    3. 예외) 도착지가 국내 또는 TSA, SHA: 인천 -> 김포로 변경
    4. 그밖의 국제선: 그대로 유지
  */
  const depCode = itinerary[0].departureCity?.code;
  if (
    tripType === TripType.OW &&
    depCode === AP_CODES.INCHEON &&
    targetCity.code === AP_CODES.INCHEON
  )
    return itinerary;
  if (tripType === TripType.MD) return itinerary;
  if (depCode === AP_CODES.INCHEON && targetCity.isDomestic)
    return changeToGimpo(itinerary);
  const settedDepartureCity = lastDepartureSearchStorage.get();
  const exceptionalApCode = EXCEPTIONAL_AP_CODES[targetCity.code] || false;
  if (settedDepartureCity || exceptionalApCode === AP_CODES.BOTH)
    return itinerary;
  if (depCode === AP_CODES.INCHEON && exceptionalApCode === AP_CODES.GIMPO)
    return changeToGimpo(itinerary);
  return itinerary;
};

export interface ISearchParams {
  tripType: TripType;
  activeIndex: number;
  itinerary: ISearchParamItinerary[];
  passengers: IPassengers;
  seatClass: SeatClass;
  isDirect: boolean;
  departureMonths?: Set<string>;
  stayLength?: string;
}

interface IContextValue {
  searchParams: ISearchParams;
  isDomestic: boolean;
  isToBizTrip: boolean;
  setSearchParams: Dispatch<SetStateAction<ISearchParams>>;
  setActiveIndex(index: number): void;
  setDepartureCity(departureCity: ICity, index?: number): void;
  setArrivalCity(arrivalCity: ICity, index?: number): void;
  setDepartureDate(departureDate?: Days | string, index?: number): void;
  setDepartureDates(departureDates: (Days | string)[]): void;
  setEverywhereSearch: (arrivalCity?: ICity) => void;
  setIsToBizTrip: Dispatch<SetStateAction<boolean>>;
  setPassengers(personType: string, diff: number): void;
  setSeatClass(seatClass: SeatClass): void;
  setStayLength(stayLength: string): void;
  setTripType(tripType: TripType): void;
  toggleDirect(): void;
}

const defaultSearchParams = {
  tripType: TripType.RT,
  activeIndex: 0,
  itinerary: [
    { departureCity: AIRPORTS.INCHEON },
    { arrivalCity: AIRPORTS.INCHEON },
  ],
  passengers: { adult: 1, child: 0, infant: 0 },
  seatClass: SeatClass.Y,
  isDirect: false,
  departureMonths: new Set<string>(),
  stayLength: '',
};

const SearchParamsContext = React.createContext<IContextValue>({
  searchParams: defaultSearchParams,
  isDomestic: false,
  isToBizTrip: false,
  setSearchParams: () => {},
  setActiveIndex: () => {},
  setDepartureCity: () => {},
  setArrivalCity: () => {},
  setDepartureDate: () => {},
  setDepartureDates: () => {},
  setEverywhereSearch: () => {},
  setIsToBizTrip: () => {},
  setPassengers: () => {},
  setSeatClass: () => {},
  setStayLength: () => {},
  setTripType: () => {},
  toggleDirect: () => {},
});

const SearchParamsContextProvider = ({
  children,
  isDomestic: isDefaultDomestic = false,
}: {
  children: React.ReactNode;
  isDomestic?: boolean;
}) => {
  const [searchParams, setSearchParams] =
    useState<ISearchParams>(defaultSearchParams);
  const [isToBizTrip, setIsToBizTrip] = useState(false);
  const isDomestic = searchParams.itinerary.every(
    ({ departureCity, arrivalCity }) =>
      departureCity && arrivalCity
        ? departureCity.isDomestic &&
          arrivalCity.isDomestic &&
          !(
            departureCity.code === AP_CODES.INCHEON &&
            arrivalCity.code === AP_CODES.INCHEON
          )
        : isDefaultDomestic,
  );
  const prevIsDomestic = usePrevious(isDomestic);

  const setTripType = (tripType: TripType) => {
    setSearchParams(prev => {
      let isDirect = prev.isDirect;
      let itinerary = [...prev.itinerary];
      let stayLength = prev.stayLength;
      switch (tripType) {
        case TripType.EVERYWHERE:
          itinerary = [
            {
              ...itinerary[0],
              departureCity: AIRPORTS.SEOUL,
              arrivalCity: AIRPORTS.EVERYWHERE,
            },
            {
              ...itinerary[1],
              departureCity: AIRPORTS.EVERYWHERE,
              arrivalCity: AIRPORTS.SEOUL,
            },
          ];
          stayLength = '';
          break;
        case TripType.OW:
          itinerary = itinerary.slice(0, 1);
          stayLength = '';
          break;
        case TripType.MD:
          isDirect = false;
          if (itinerary.length < 2) {
            itinerary.length = 2;
            itinerary[1] = {
              ...itinerary[1],
              departureCity: itinerary[0].arrivalCity,
              arrivalCity: itinerary[0].departureCity,
            };
          }
          stayLength = '';
          break;
        case TripType.RT:
          itinerary.length = 2;
          itinerary[1] = {
            ...itinerary[1],
            departureCity: itinerary[0].arrivalCity,
            arrivalCity: itinerary[0].departureCity,
          };
          break;
        default:
          break;
      }
      return { ...prev, isDirect, itinerary, stayLength, tripType };
    });
  };

  const setEverywhereSearch = (
    arrivalCity: ICity = (AIRPORTS.EVERYWHERE = AIRPORTS.EVERYWHERE),
  ) => {
    setSearchParams(prev => ({
      ...prev,
      itinerary: [
        { ...prev.itinerary[0], departureCity: AIRPORTS.SEOUL, arrivalCity },
        {
          ...prev.itinerary[1],
          departureCity: arrivalCity,
          arrivalCity: AIRPORTS.SEOUL,
        },
      ],
      tripType: TripType.EVERYWHERE,
    }));
  };

  const setActiveIndex = (activeIndex: number) => {
    setSearchParams(prev => ({ ...prev, activeIndex }));
  };

  const setDepartureCity = (departureCity: ICity, idx?: number) => {
    setSearchParams(prev => {
      const { activeIndex, tripType, itinerary: prevItinerary } = prev;
      const index = idx || activeIndex;
      const itinerary = [...prevItinerary];
      itinerary.splice(index, 1, { ...prev.itinerary[index], departureCity });
      if (tripType === TripType.RT) {
        const opossiteIndex = index === 0 ? 1 : 0;
        itinerary[opossiteIndex] = {
          ...prevItinerary[opossiteIndex],
          arrivalCity: departureCity,
        };
      }
      lastDepartureSearchStorage.set(departureCity.code);
      return { ...prev, itinerary };
    });
  };

  const setArrivalCity = (arrivalCity: ICity, idx?: number) => {
    setSearchParams(prev => {
      const { activeIndex, tripType, itinerary: prevItinerary } = prev;
      const index = idx || activeIndex;
      const itinerary = prevItinerary.map(iti => ({ ...iti }));
      itinerary.splice(index, 1, { ...prev.itinerary[index], arrivalCity });
      const triggeredIndex =
        tripType === TripType.RT
          ? Math.abs(index - 1)
          : tripType === TripType.MD && index < itinerary.length - 1
            ? index + 1
            : null;
      if (
        triggeredIndex !== null &&
        (tripType === TripType.RT || !itinerary[triggeredIndex]?.departureCity)
      )
        itinerary[triggeredIndex] = {
          ...(itinerary[triggeredIndex] || {}),
          departureCity: arrivalCity,
        };
      return {
        ...prev,
        itinerary: getExceptionHandledItinerary(
          itinerary,
          tripType,
          arrivalCity,
        ),
      };
    });
  };

  const setDepartureDate = (d?: Days | string, idx?: number) => {
    const departureDate = getValidDate(d);
    if (departureDate === false) return;
    setSearchParams(prev => {
      const index = idx || prev.activeIndex;
      const itinerary = [...prev.itinerary];
      itinerary.splice(index, 1, { ...prev.itinerary[index], departureDate });
      return { ...prev, itinerary };
    });
  };

  const setDepartureDates = (departureDates: (Days | string)[]) => {
    const dates = getValidDates(departureDates);
    if (!dates) return;
    setSearchParams(prev => ({
      ...prev,
      itinerary: prev.itinerary.map((iti, i) => ({
        ...iti,
        departureDate: dates[i],
      })),
    }));
  };

  const setPassengers = (personType: string, diff: number) => {
    setSearchParams(prev => ({
      ...prev,
      passengers: {
        ...prev.passengers,
        [personType]: (prev.passengers[personType] || 0) + diff,
      },
    }));
  };

  const setSeatClass = (seatClass: SeatClass) => {
    setSearchParams(prev => ({ ...prev, seatClass }));
  };

  const setStayLength = (duration: string) => {
    setSearchParams(prev => ({ ...prev, stayLength: duration }));
  };

  const toggleDirect = () => {
    setSearchParams(prev => ({ ...prev, isDirect: !prev.isDirect }));
  };

  useEffect(() => {
    if (prevIsDomestic !== undefined && prevIsDomestic !== isDomestic) {
      setSearchParams(prev => ({
        ...prev,
        isDirect: isDomestic ? false : prev.isDirect,
        seatClass: isDomestic ? SeatClass.YC : SeatClass.Y,
        stayLength: isDomestic ? '' : prev.stayLength,
      }));
    }
  }, [isDomestic]);

  return (
    <SearchParamsContext.Provider
      value={{
        searchParams,
        isDomestic,
        isToBizTrip,
        setSearchParams,
        setActiveIndex,
        setDepartureCity,
        setArrivalCity,
        setDepartureDate,
        setDepartureDates,
        setEverywhereSearch,
        setIsToBizTrip,
        setPassengers,
        setSeatClass,
        setStayLength,
        setTripType,
        toggleDirect,
      }}
    >
      {children}
    </SearchParamsContext.Provider>
  );
};

const useSearchParamsContext = () => useContext(SearchParamsContext);

const getSearchType = (
  tripType: TripType,
  isDomestic: boolean,
  isInternational: boolean,
  { departureCity, arrivalCity }: ISearchParamItinerary,
) => {
  if (tripType === TripType.EVERYWHERE) return SearchType.EVERYWHERE;
  if (
    arrivalCity?.code === departureCity?.code &&
    arrivalCity?.code === AP_CODES.INCHEON
  )
    return SearchType.INTERNATIONAL;
  if (isDomestic) return SearchType.DOMESTIC;
  if (!isDomestic || isInternational) return SearchType.INTERNATIONAL;
  return undefined;
};

const getLinkPair = (
  itinerary: ISearchParamItinerary[],
  passengers: IPassengers,
  seatClass: string,
  isDirect: boolean,
  searchType: string | undefined,
  stayLength?: string,
  departureMonths?: Set<string>,
  continents?: string,
): [string, string] => {
  if (searchType === SearchType.EVERYWHERE) {
    if (itinerary.length === 0) return ['', ''];
    const [departure, arrival] = itinerary;
    if (!departure.departureCity) return ['', ''];
    if (departure.departureDate && arrival.departureDate) {
      const params = new URLSearchParams();
      if (
        departure.arrivalCity &&
        /^[A-Z]{2}$/.test(departure.arrivalCity.code)
      ) {
        params.append('country', departure.arrivalCity.code);
      } else if (continents) {
        params.append('continents', continents);
      }
      const query = params.toString();
      return [
        `/flights/everywhere/daily/[departure]/[schedule]?${query}`,
        `/flights/everywhere/daily/${departure.departureCity.code}/${departure.departureDate}-${arrival.departureDate}?${query}`,
      ];
    }
    const params = new URLSearchParams();
    if (departureMonths?.size) {
      params.append('months', Array.from(departureMonths).toString());
    }
    if (
      departure.arrivalCity &&
      /^[A-Z]{2}$/.test(departure.arrivalCity.code)
    ) {
      params.append('country', departure.arrivalCity.code);
    } else if (continents) {
      params.append('continents', continents);
    }
    const query = params.toString();
    return [
      `/flights/everywhere/monthly/[departure]?${query}`,
      `/flights/everywhere/monthly/${departure.departureCity.code}?${query}`,
    ];
  }

  const { adult, child, infant } = passengers;
  const queries = new URLSearchParams();
  if (adult) queries.set('adult', `${adult}`);
  if (child) queries.set('child', `${child}`);
  if (infant) queries.set('infant', `${infant}`);
  if (isDirect) queries.set('isDirect', `${isDirect}`);
  if (seatClass) queries.set('fareType', seatClass);
  const queryParams = queries.toString();
  if (searchType === SearchType.DOMESTIC) {
    return [
      `/flights/domestic/[...routes]?${queryParams}`,
      `/flights/domestic/${stringifyItinerary(itinerary)}?${queryParams}`,
    ];
  }
  if (searchType === SearchType.INTERNATIONAL) {
    return [
      `/flights/international/[...routes]?${queryParams}`,
      `/flights/international/${stringifyItinerary(
        itinerary,
        stayLength,
      )}?${queryParams}`,
    ];
  }
  return ['', ''];
};

const getSearchLinkPair = (): [string, string] => {
  const router = useRouter();
  const {
    query: { continents },
  } = router;
  if (!process.browser) return ['', ''];
  const routePaths = window.location.pathname.split('/'); // international list에서 활용.
  const isInternational = routePaths[2] === 'international';
  const value = useContext(SearchParamsContext);
  if (!value) return ['', ''];
  const { searchParams, isDomestic } = value;
  const {
    itinerary,
    passengers,
    seatClass,
    isDirect,
    stayLength,
    departureMonths,
  } = searchParams;
  const searchType = getSearchType(
    searchParams.tripType,
    isDomestic,
    isInternational,
    itinerary[0],
  );
  return getLinkPair(
    itinerary,
    passengers,
    seatClass,
    isDirect,
    searchType,
    stayLength,
    departureMonths,
    continents?.toString(),
  );
};

const bizCabinClassMap = new Map<SeatClass, string>([
  [SeatClass.Y, 'ECONOMY_CLASS'],
  [SeatClass.P, 'PREMIUM_ECONOMY_CLASS'],
  [SeatClass.C, 'BUSINESS_CLASS'],
  [SeatClass.F, 'FIRST_CLASS'],
]);

const getBizFlightUrl = ({
  itinerary,
  passengers,
  seatClass,
  stayLength,
  isDirect,
}: {
  itinerary: ISearchParamItinerary[];
  passengers: IPassengers;
  seatClass: SeatClass;
  stayLength: string | undefined;
  isDirect: boolean;
}) => {
  const routes = itinerary.map(
    (
      {
        arrivalAirport,
        arrivalCity,
        departureAirport,
        departureCity,
        departureDate,
      },
      index,
    ) =>
      `${departureCity?.code ?? departureAirport}-${arrivalCity?.code ?? arrivalAirport}-${
        stayLength && index === 1 ? '' : departureDate
      }`,
  );
  const queryParam = Object.entries({
    adultCount: passengers.adult,
    childCount: passengers.child,
    infantCount: passengers.infant,
    cabinClass: bizCabinClassMap.get(seatClass) || '',
    openReturnDays:
      routes.length === 2 &&
      stayLength &&
      stayLength.slice(0, stayLength.length - 1), // 30D -> 30
    isDirect,
  })
    .filter(([, value]) => value)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');
  return `${config.bizflight}/${routes.join(
    '/',
  )}?${queryParam}&signupRedirect=true`;
};

export {
  AIRPORTS,
  AP_CODES,
  SearchParamsContext,
  SearchParamsContextProvider,
  SearchType,
  SeatClass,
  TripType,
  defaultSearchParams,
  getBizFlightUrl,
  getLinkPair,
  getPassengerDesc,
  getSearchLinkPair,
  getSeatClassMap,
  getSeatDesc,
  searchTypeKoreanMap,
  useSearchParamsContext,
};
